import { syncMessageSequence } from "@/src/helpers/messageSequence";
import { useIsMobile } from "@/src/helpers/useIsMobile";
import { type DoChatLocale, getDoChatMessages } from "@do/do-chat-messages";
import { sleep } from "@do/next-utils";
import {
  type AnalyticsEventType,
  type BlockText,
  type APIDochatConfigAnswer,
  type ChatbotParams,
  type ChatCustomBlock,
  type ChatFastAction,
  type ChatMessage,
  type Locale,
  type SatisfactionForm,
  type SurveyAnswer,
  assertDefined,
  ChatCustomBlockTypes,
  DoChatAction,
} from "@do/shared-types";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { GetServerSideProps } from "next";
import { NextIntlClientProvider } from "next-intl";
import Head from "next/head";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getGenericErrorMessage,
  getMessageForError,
  getOutOfCreditsErrorMessage,
} from "../src/errors";
import {
  fetchDochatConfig,
  fetchOutOfCredits,
  fetchSessionEventTypes,
  submitSurvey,
  trackEvent,
} from "../src/gateways/doChatApi";
import {
  appendHttpsSchemeIfNecessary,
  buildRewriteConversationContext,
  getQueryParams,
  localeSwitcher,
  rewriteUrl,
} from "../src/helpers";
import { resolveNestedKey } from "../src/helpers/resolveNestedKey";
import { buildSolutionClickCommand, getSolutionClickToReport } from "../src/helpers/solutionClick";
import { buildSubmitMetadata } from "../src/helpers/submitMetadata";
import {
  sendQuestionToDoBot,
  sendQuestionToDoChat,
  StreamedMessagesReceiver,
} from "../src/SenderReceiver";
import type { DoBotResponse, ForDoBotPayload, FromDoBotBlock } from "../src/types/common";

import { Chat, type ChatProps } from "@do/ui-chat-v2";
import { forgetStoredTicket, readStoredTicket, storeTicket } from "../src/auth/ticketStorage";
import { setChatTicket } from "../src/gateways/chatHttp";
import { LoginScreen } from "../src/ui/auth/LoginScreen";

const LoadingScreen = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div
      aria-label="Loading"
      className="size-10 animate-spin rounded-full border-4 border-current border-t-transparent opacity-60"
      role="progressbar"
    />
  </div>
);

const FAKE_STREAMING_NB_STREAMED_LETTERS_AT_ONCE = 3;
const FAKE_STREAMING_DELAY_MS = 8;
const TIMEOUT_AFTER_GREETING_BEFORE_DISABLING_SSE_MS = 3000;
// Routes the conversation to the standard "bad feedback" node. The chatbot answers to
// this command, and to its `bad_rate` / `deny` aliases, with that node's response.
const BAD_FEEDBACK_COMMAND = "/bad_feedback";
const DEFAULT_USED_SOURCE_FAVICON_URL =
  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIGFyaWEtaGlkZGVuPSJ0cnVlIiB2aWV3Qm94PSIwIDAgMjQgMjQiIGZpbGw9IiM4MjgyODIiPjxwYXRoIGQ9Ik0xMiAyQzYuNDggMiAyIDYuNDggMiAxMnM0LjQ4IDEwIDEwIDEwIDEwLTQuNDggMTAtMTBTMTcuNTIgMiAxMiAybS0xIDE3LjkzYy0zLjk1LS40OS03LTMuODUtNy03LjkzIDAtLjYyLjA4LTEuMjEuMjEtMS43OUw5IDE1djFjMCAxLjEuOSAyIDIgMnptNi45LTIuNTRjLS4yNi0uODEtMS0xLjM5LTEuOS0xLjM5aC0xdi0zYzAtLjU1LS40NS0xLTEtMUg4di0yaDJjLjU1IDAgMS0uNDUgMS0xVjdoMmMxLjEgMCAyLS45IDItMnYtLjQxYzIuOTMgMS4xOSA1IDQuMDYgNSA3LjQxIDAgMi4wOC0uOCAzLjk3LTIuMSA1LjM5Ij48L3BhdGg+PC9zdmc+";

const CDN_URL = process.env.NX_ENVIRONMENT_CDN_URL ?? "https://cdn.dial-once.com";

function getDomainFromSiteUrl(siteUrl: string): string {
  try {
    const hostname = new URL(appendHttpsSchemeIfNecessary(siteUrl)).hostname.replace(/^www\./, "");
    return hostname || siteUrl;
  } catch {
    return siteUrl;
  }
}

function filterNonEmptyUsedSourcesMetadatas(
  list: ChatMessage["used_sources_metadatas"]
): ChatMessage["used_sources_metadatas"] {
  if (list == null || list.length === 0) return undefined;
  const kept = list
    .map((item) => {
      const siteUrl = item.site_url.trim();

      if (!siteUrl) return item;

      return {
        ...item,
        site_name: item.site_name.trim() || getDomainFromSiteUrl(siteUrl),
        page_title: item.page_title.trim() || siteUrl,
        favicon_url: item.favicon_url.trim() || DEFAULT_USED_SOURCE_FAVICON_URL,
      };
    })
    .filter((item) => {
      const hasText =
        item.site_name.trim() ||
        item.page_title.trim() ||
        item.description.trim() ||
        item.favicon_url.trim() ||
        item.site_url.trim();
      return Boolean(hasText);
    });
  return kept.length > 0 ? kept : undefined;
}

/**
 * Only generative answers are token-streamed, so a response that arrived through
 * the SSE pipeline can be reported as generative to the UI. Answers received
 * without streaming (SSE disabled or fallen back to a direct call) are not
 * flagged, even when generative.
 */
function markAsGenerative(message: ChatMessage): ChatMessage {
  if (typeof message.metadata === "string") return message;
  return {
    ...message,
    metadata: [...(message.metadata ?? []), { generative_response: true }],
  };
}

export const DochatConfigPage = () => {
  const [chatbotParams, setChatbotParams] = useState<ChatbotParams | null>(null);
  const [satisfactionForm, setSatisfactionForm] = useState<SatisfactionForm | null>(null);
  const [liveChatEndedAt, setLiveChatEndedAt] = useState<number | null>(null);
  // Separator closing the last livechat: back navigation cannot go past it.
  const [liveChatEndMessageId, setLiveChatEndMessageId] = useState<string | null>(null);
  const [analyticId, setAnalyticId] = useState<string | null>(null);
  // Set while the chat requires a sign-in it does not have: its login screen is shown instead.
  const [authGate, setAuthGate] = useState<Pick<APIDochatConfigAnswer, "config" | "auth"> | null>(
    null
  );
  // Incremented on every conversation reset, to fetch the start parameters of a new conversation.
  const [conversationCount, setConversationCount] = useState(0);
  const [currentLocale, setCurrentLocale] = useState<Locale | null>(null);
  const [maxUserMessageLength, setMaxUserMessageLength] = useState(511);
  const [disabled, setDisabled] = useState(false);
  const [isStreamingDisabled, setIsStreamingDisabled] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mergedCtxData, setMergedCtxData] = useState<Record<string, unknown> | null>(null);
  // Intent and entities resolved server-side from both the dynamic and the static contexts.
  const [intent, setIntent] = useState<string | null>(null);
  const [entities, setEntities] = useState<Record<string, unknown> | null>(null);
  const [isOutOfCredits, setIsOutOfCredits] = useState(false);
  const didReceiveResponseToInitialGreeting = useRef<boolean>(false);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const initialGreetingTimeoutTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const isInLiveChatModeFromBack = useRef(false);
  const isLiveChatActiveRef = useRef<boolean | null>(null);
  const messagesLengthRef = useRef(0);
  const conversationGenerationRef = useRef(0);
  const streamedMessage = useRef<ChatMessage | null>(null);
  const lastUserMessageRef = useRef<HTMLDivElement | null>(null);
  // `onlyLastMessage` mode replaces the `messages` array with only the latest
  // bot response, so the conversation history needed for the "back" button is
  // kept here as a stack of previously displayed snapshots.
  const currentMessagesRef = useRef<ChatMessage[]>([]);
  const onlyLastMessageHistoryRef = useRef<ChatMessage[][]>([]);
  const [onlyLastMessageHistoryLength, setOnlyLastMessageHistoryLength] = useState(0);
  const [chatLocale, setChatLocale] = useState<DoChatLocale>("fr");
  const currentMessages = useMemo(() => getDoChatMessages(chatLocale), [chatLocale]);
  const tCommon = useCallback(
    (key: string) => resolveNestedKey(currentMessages["do-chat-common"], key),
    [currentMessages]
  );
  const tV2 = useCallback(
    (key: string) => resolveNestedKey(currentMessages["do-chat-v2"], key),
    [currentMessages]
  );
  const router = useRouter();
  const {
    brand: brandFromQueryParams,
    callingURL, // used to find the starter intent according to the calling URL for widget integration, this is a static context
    chatbotStaticCtxMapKey, // used to find the starter intent according to the chatbot context for widget integration, this is a static context
    dochatConfigId,
    digitalDynamicCtx, // used to find the starter intent according to the dynamic context for widget integration, this is a dynamic context
    isClosable,
    hasNoHeader,
    isTestingMode,
    isEditingMode,
    isUrlRewriteDebug,
    localeFromQueryParams,
    pinCode, // used to find the starter intent according to the context retrieved from contrack, it's for SMS to Bot integration, this is a dynamic context
    positionId, // used to find the starter intent according to the context associated with a voice position, it's for SMS to Bot integration, this is a static context
    sessionId,
    simDatetime, // moment the console testing page plays the conversation at, testing mode only
    theming,
  } = useMemo(() => getQueryParams(router), [router]);
  // The sign-in ticket, kept in the browser so that the chat starts signed in again.
  const [authTicket, setAuthTicket] = useState(() => readStoredTicket(dochatConfigId));
  useEffect(() => {
    // Every chat route of the conversation then carries it.
    setChatTicket(authTicket);
  }, [authTicket]);
  const isMobile = useIsMobile();
  // stores the id of the first of the new arriving messages, used to scroll to it
  const lastMessageIdRef = useRef<string | null>(null);

  const urlRewriteConfig = chatbotParams?.urlRewriteConfig;
  useEffect(() => {
    if (urlRewriteConfig && urlRewriteConfig.enabled !== false && !callingURL) {
      // eslint-disable-next-line no-console
      console.warn(
        "[dialonce-url-rewrite] urlRewriteConfig is configured but the host URL is missing " +
          "(only the widget sends it, as `calling-url`). transferParams/rewriteKey actions will be no-ops."
      );
    }
  }, [urlRewriteConfig, callingURL]);
  const rewrite = useCallback<NonNullable<ChatProps["rewriteUrl"]>>(
    (url) => {
      if (!url || !urlRewriteConfig) return url;
      const conversationContext = buildRewriteConversationContext({
        intent,
        entities,
        mergedCtxData,
        sessionId,
      });
      return rewriteUrl(url, urlRewriteConfig, callingURL, conversationContext, {
        debug: isUrlRewriteDebug,
      });
    },
    [urlRewriteConfig, callingURL, intent, entities, mergedCtxData, sessionId, isUrlRewriteDebug]
  );

  const wishedLocale = useRef(localeFromQueryParams);
  useEffect(() => {
    messagesLengthRef.current = syncMessageSequence(messagesLengthRef.current, messages);
    currentMessagesRef.current = messages;
  }, [messages]);

  const resetState = useCallback(() => {
    conversationGenerationRef.current += 1;
    setConversationCount((count) => count + 1);
    clearTimeout(initialGreetingTimeoutTimer.current);
    streamedMessagesReceiver.current?.close();
    streamedMessagesReceiver.current = undefined;
    setChatbotParams(null);
    setSatisfactionForm(null);
    setAnalyticId(null);
    setMessages([]);
    setIsLoading(false);
    setMergedCtxData(null);
    lastMessageIdRef.current = null;
    messagesLengthRef.current = 0;
    currentMessagesRef.current = [];
    onlyLastMessageHistoryRef.current = [];
    setOnlyLastMessageHistoryLength(0);
    streamedMessage.current = null;
    didReceiveResponseToInitialGreeting.current = false;
    initialGreetingTimeoutTimer.current = undefined;
    setIsStreamingDisabled(false);
    setDisabled(true);
    isLiveChatActiveRef.current = null;
    setLiveChatEndedAt(null);
    setLiveChatEndMessageId(null);
  }, []);

  const doBotPayloadRef = useRef<ForDoBotPayload>({
    message: "",
    metadata: {
      context: {
        pinCode: null,
        static_data: null,
      },
    },
    sender: "",
  });

  const doBotUrlRef = useRef<string | null>(null);
  const streamedMessagesReceiver = useRef<StreamedMessagesReceiver>(undefined);

  const lastUserMessageId = useMemo(() => {
    return messages.findLast((message) => message.user)?.id;
  }, [messages]);

  const isOnlyLastMessageDisplayed = useMemo(
    () => chatbotParams?.onlyLastMessage && !isInLiveChatModeFromBack.current,
    [chatbotParams?.onlyLastMessage]
  );

  const showLoadingDots = useCallback(() => {
    setIsLoading(true);
  }, []);
  const hideLoadingDots = useCallback(() => {
    setIsLoading(false);
  }, []);
  const refreshMessages = useCallback(() => {
    setMessages((messages) => [...messages]);
  }, []);

  const displayBlockWithFakeStreamingEffect = useCallback(
    async (block: BlockText, expectedGeneration = conversationGenerationRef.current) => {
      const textArray = block.text;
      block.text = "";
      for (let i = 0; i < textArray.length; i += FAKE_STREAMING_NB_STREAMED_LETTERS_AT_ONCE) {
        if (expectedGeneration !== conversationGenerationRef.current) {
          return;
        }
        const substr = textArray.slice(i, i + FAKE_STREAMING_NB_STREAMED_LETTERS_AT_ONCE);
        block.text += substr;
        refreshMessages();
        await sleep(FAKE_STREAMING_DELAY_MS);
      }
    },
    [refreshMessages]
  );

  const displayMessageWithFakeStreamingEffect = useCallback(
    async (message: ChatMessage, expectedGeneration = conversationGenerationRef.current) => {
      if (expectedGeneration !== conversationGenerationRef.current) {
        return;
      }
      // Show a message without its blocks
      const fakeStreamedMessage = { ...message };
      const streamedBlocks = fakeStreamedMessage.blocks;
      fakeStreamedMessage.blocks = [];
      // Display the message before the loading dots
      showLoadingDots();
      if (isOnlyLastMessageDisplayed) {
        setMessages(() => [fakeStreamedMessage]);
      } else setMessages((messages) => [...messages, fakeStreamedMessage]);
      // Show the blocks one by one
      if (streamedBlocks) {
        for (const block of streamedBlocks ?? []) {
          if (expectedGeneration !== conversationGenerationRef.current) {
            return;
          }
          fakeStreamedMessage.blocks.push(block);
          if (block.type === ChatCustomBlockTypes.text) {
            await displayBlockWithFakeStreamingEffect(block, expectedGeneration);
          }
          refreshMessages();
        }
      }
      hideLoadingDots();
    },
    [
      displayBlockWithFakeStreamingEffect,
      refreshMessages,
      isOnlyLastMessageDisplayed,
      showLoadingDots,
      hideLoadingDots,
    ]
  );

  const displayMessagesWithFakeStreamingEffect = useCallback(
    async (newMessages: ChatMessage[], expectedGeneration = conversationGenerationRef.current) => {
      for (const message of newMessages) {
        if (expectedGeneration !== conversationGenerationRef.current) {
          return;
        }
        await displayMessageWithFakeStreamingEffect(message, expectedGeneration);
      }
    },
    [displayMessageWithFakeStreamingEffect]
  );

  const clearInitialGreetingTimeout = useCallback(() => {
    if (!didReceiveResponseToInitialGreeting.current) {
      didReceiveResponseToInitialGreeting.current = true;
      clearTimeout(initialGreetingTimeoutTimer.current);
      initialGreetingTimeoutTimer.current = undefined;
    }
  }, []);

  const handleLiveChatActiveChange = useCallback(
    (isActive?: boolean) => {
      if (isActive === undefined || isLiveChatActiveRef.current === isActive) return;
      isLiveChatActiveRef.current = isActive;

      messagesLengthRef.current += 1;
      const text = isActive
        ? tV2("chat.messages.live-chat.start")
        : tV2("chat.messages.live-chat.end");
      const id = `message-${isActive ? "live-chat-start" : "live-chat-end"}-${
        messagesLengthRef.current
      }`;

      if (isActive === false) {
        setLiveChatEndedAt(Date.now());
        setLiveChatEndMessageId(id);
      }

      // The advisor conversation cannot be resumed, so `onlyLastMessage`'s "back"
      // history must not lead to anything before it.
      onlyLastMessageHistoryRef.current = [];
      setOnlyLastMessageHistoryLength(0);

      setMessages((previousMessages) => [
        ...previousMessages,
        {
          blocks: [
            {
              type: ChatCustomBlockTypes.separator,
              text,
              id: `${id}-block-0`,
            },
          ],
          user: false,
          id,
        },
      ]);
    },
    [setMessages, tV2]
  );

  const handleSendNewMessagesToCrai = useCallback(
    (messagesToCrai: Record<string, unknown>[]) => {
      return isTestingMode
        ? window.parent.postMessage(
            {
              action: DoChatAction.newSourcesToAdd,
              newMessages: messagesToCrai,
            },
            "*"
          )
        : null;
    },
    [isTestingMode]
  );

  // Refetched after every bot response.
  const { refetch: refetchOutOfCredits } = useQuery({
    queryKey: ["outOfCredits", analyticId],
    queryFn: () => {
      assertDefined(analyticId);
      return fetchOutOfCredits(analyticId);
    },
    enabled: false,
    onSuccess: (outOfCreditsRes: boolean) => {
      setIsOutOfCredits(outOfCreditsRes);
      if (outOfCreditsRes) {
        setMessages((prev) => [...prev, getOutOfCreditsErrorMessage(tCommon)]);
      }
    },
  });

  const processBlockButtons = useCallback((block: ChatCustomBlock) => {
    if (block.type !== ChatCustomBlockTypes.buttons) return {};
    const buttonsWithoutAutorun: ChatFastAction[] = [];
    const autorunCodeButtons: ChatFastAction[] = [];
    block.buttons.forEach((button) => {
      if (button.code && button.autorun) {
        autorunCodeButtons.push(button);
      } else {
        buttonsWithoutAutorun.push(button);
      }
    });
    if (autorunCodeButtons.length > 0) {
      const scriptElement = document.createElement("script");
      scriptElement.textContent = autorunCodeButtons.map((button) => button.code).join("\n");
      document.head.appendChild(scriptElement);
    }
    return { buttons: buttonsWithoutAutorun };
  }, []);

  const displayDoBotResponse = useCallback(
    async (
      finalPayload: DoBotResponse,
      isInLiveChatMode?: boolean,
      expectedGeneration = conversationGenerationRef.current
    ) => {
      if (expectedGeneration !== conversationGenerationRef.current) {
        return;
      }
      void refetchOutOfCredits();

      isInLiveChatModeFromBack.current = isInLiveChatMode ?? false;
      const arrivingMessages: ChatMessage[] = finalPayload
        .map((doBotBlock: FromDoBotBlock, index) => {
          messagesLengthRef.current += 1;
          const messageId = `message-${messagesLengthRef.current}`;
          if (index === 0) {
            lastMessageIdRef.current = messageId;
          }

          const messageBlocks = doBotBlock.custom?.blocks
            ?.map((block, blockIndex) => {
              const buttonsWithoutAutorun = processBlockButtons(block);
              // if the block is a buttons block and has no buttons without autorun, skip the block
              if (
                block.type === ChatCustomBlockTypes.buttons &&
                buttonsWithoutAutorun.buttons?.length === 0
              )
                return null;
              return {
                ...block,
                ...buttonsWithoutAutorun,
                id: `${messageId}-block-${blockIndex}`,
              };
            })
            .filter((block) => block !== null);
          const messageContainers = doBotBlock.custom?.containers
            ?.map((container, containerIndex) => {
              return {
                ...container,
                id: `${messageId}-container-${containerIndex}`,
              };
            })
            .filter((container) => container !== null);

          if (messageBlocks?.length === 0) return null;

          return {
            blocks: messageBlocks,
            containers: messageContainers,
            id: messageId,
            events: doBotBlock.custom?.events || {},
            metadata: doBotBlock.custom?.metadata,
            used_sources_metadatas: filterNonEmptyUsedSourcesMetadatas(
              doBotBlock.custom?.used_sources_metadatas
            ),
            ...(doBotBlock.custom?.advisor ? { advisor: true } : {}),
          };
        })
        .filter((message) => message !== null);

      if (arrivingMessages.length === 0) {
        setDisabled(false);
        hideLoadingDots();
        return;
      }

      if (isTestingMode) {
        handleSendNewMessagesToCrai(
          arrivingMessages.map((message) => {
            return {
              messageId: message.id,
              userQuestion: doBotPayloadRef.current.message ?? "",
              metadata: (message.metadata?.[0] as Record<string, unknown>)?.generative_response,
            };
          })
        );
      }

      if (streamedMessage.current) {
        // Overwrite the streamedMessage with this new final message
        setMessages((messages) => {
          const newMessages = [
            ...messages.filter((msg) => msg !== streamedMessage.current),
            ...arrivingMessages.map(markAsGenerative),
          ];
          streamedMessage.current = null;
          return newMessages;
        });
      } else {
        // No streamed message: display received messages,
        // if option is active, do it with a streamed effect
        if (chatbotParams?.simulateStreaming) {
          await displayMessagesWithFakeStreamingEffect(arrivingMessages, expectedGeneration);
          if (expectedGeneration !== conversationGenerationRef.current) {
            return;
          }
        } else {
          // Hack for spidy '\\n': Replace them with '\n'
          arrivingMessages
            .filter((msg) => msg?.blocks)
            .forEach((msg) =>
              msg.blocks
                ?.filter((block) => block.type === ChatCustomBlockTypes.text)
                .forEach((block) => {
                  block.text = block.text.replaceAll("\\n", "\n");
                })
            );
          if (isOnlyLastMessageDisplayed) {
            setMessages([...arrivingMessages]);
          } else {
            setMessages((messages) => [...messages, ...arrivingMessages]);
          }
        }
      }
      if (expectedGeneration !== conversationGenerationRef.current) {
        return;
      }
      setDisabled(false);
      hideLoadingDots();
    },
    [
      chatbotParams?.simulateStreaming,
      displayMessagesWithFakeStreamingEffect,
      handleSendNewMessagesToCrai,
      hideLoadingDots,
      isOnlyLastMessageDisplayed,
      isTestingMode,
      processBlockButtons,
      refetchOutOfCredits,
    ]
  );

  const listenForIncomingMessages = useCallback(() => {
    if (isStreamingDisabled) {
      return;
    }
    const currentConversationGeneration = conversationGenerationRef.current;
    streamedMessagesReceiver.current?.close();
    streamedMessagesReceiver.current = new StreamedMessagesReceiver(
      doBotPayloadRef.current.sender,
      {
        receivedAnswerChunk: (messageChunk: string) => {
          if (currentConversationGeneration !== conversationGenerationRef.current) return;
          clearInitialGreetingTimeout();

          const oldBlock = (streamedMessage.current?.blocks ?? [])[0] as BlockText | undefined;
          const oldText = oldBlock?.text ?? "";

          const newBlock: ChatCustomBlock = {
            type: ChatCustomBlockTypes.text,
            text: oldText + messageChunk,
          };

          if (streamedMessage.current == null) {
            streamedMessage.current = {
              blocks: [newBlock],
              user: false,
              metadata: undefined,
              id: "streamed-msg",
            };
            showLoadingDots();
            if (isOnlyLastMessageDisplayed) {
              setMessages([streamedMessage.current]);
            } else {
              setMessages((messages) => [...messages, streamedMessage.current as ChatMessage]);
            }
          } else {
            if (streamedMessage.current?.blocks) {
              streamedMessage.current.blocks[0] = newBlock;
            }
            refreshMessages();
          }
        },

        receivedFinalMessage: (
          finalPayload: DoBotResponse,
          isInLiveChatMode?: boolean,
          isLiveChatActive?: boolean
        ) => {
          if (currentConversationGeneration !== conversationGenerationRef.current) return;
          clearInitialGreetingTimeout();
          handleLiveChatActiveChange(isLiveChatActive);
          void displayDoBotResponse(finalPayload, isInLiveChatMode, currentConversationGeneration);
        },

        onError: () => {
          if (currentConversationGeneration !== conversationGenerationRef.current) return;
          streamedMessage.current = null;
          hideLoadingDots();
          if (!didReceiveResponseToInitialGreeting.current) {
            // will relaunch askDoBot without SSE
            setIsStreamingDisabled(true);
          } else {
            // Append the error message
            setMessages((messages) => [...messages, getGenericErrorMessage(tCommon)]);
            setDisabled(false);
          }
        },
      }
    );
  }, [
    isStreamingDisabled,
    clearInitialGreetingTimeout,
    handleLiveChatActiveChange,
    showLoadingDots,
    isOnlyLastMessageDisplayed,
    refreshMessages,
    displayDoBotResponse,
    hideLoadingDots,
    tCommon,
  ]);

  useEffect(() => {
    // Don't auto-focus on mobile to prevent keyboard from opening after message generation
    if (!disabled && !isMobile) {
      const input = chatInputRef?.current;
      if (input) {
        input.focus({ preventScroll: true });
      }
    }
  }, [disabled, isMobile]);

  const startInitialGreetingTimeout = useCallback(async () => {
    const currentConversationGeneration = conversationGenerationRef.current;
    initialGreetingTimeoutTimer.current = setTimeout(() => {
      if (currentConversationGeneration !== conversationGenerationRef.current) return;
      // eslint-disable-next-line no-console
      console.error("SSE fell into timeout");
      streamedMessagesReceiver.current?.close();
      streamedMessagesReceiver.current = undefined;

      setMessages((messages) => {
        const newMessages = [...messages.filter((msg) => msg !== streamedMessage.current)];
        streamedMessage.current = null;
        return newMessages;
      });

      hideLoadingDots();
      // changing isStreamingDisabled will relaunch askDoBot
      setIsStreamingDisabled(true);
    }, TIMEOUT_AFTER_GREETING_BEFORE_DISABLING_SSE_MS);
  }, [hideLoadingDots]);

  const askDoBot = useCallback(async () => {
    const currentConversationGeneration = conversationGenerationRef.current;
    try {
      if (!doBotUrlRef.current) throw new Error("Trying to use askDoBot without a do-bot URL.");
      if (!isInLiveChatModeFromBack.current) {
        showLoadingDots();
        setDisabled(true);
      } else {
        setDisabled(false);
      }
      if (isStreamingDisabled) {
        const response = await sendQuestionToDoBot(doBotPayloadRef.current);
        if (currentConversationGeneration !== conversationGenerationRef.current) return;
        await displayDoBotResponse(response, undefined, currentConversationGeneration);
      } else {
        await sendQuestionToDoChat(doBotPayloadRef.current);
        if (currentConversationGeneration !== conversationGenerationRef.current) return;
        if (!didReceiveResponseToInitialGreeting.current) {
          await startInitialGreetingTimeout();
        }
      }
    } catch (error: unknown) {
      if (currentConversationGeneration !== conversationGenerationRef.current) return;
      // eslint-disable-next-line no-console
      console.error("Error while posting question", error);
      setMessages((messages) => [...messages, getMessageForError(tCommon, error)]);
      setDisabled(false);
      hideLoadingDots();
    }
  }, [
    displayDoBotResponse,
    hideLoadingDots,
    isStreamingDisabled,
    showLoadingDots,
    startInitialGreetingTimeout,
    tCommon,
  ]);

  const { mutate: trackEventMutate } = useMutation({ mutationFn: trackEvent });
  const { mutateAsync: submitSurveyMutateAsync } = useMutation({ mutationFn: submitSurvey });

  // Event types already recorded for this analytics session (e.g. survey_displayed),
  // so the satisfaction survey is only shown once per session. `undefined` while loading.
  const { data: recordedEventTypes } = useQuery({
    queryKey: ["sessionEventTypes", analyticId],
    queryFn: () => {
      assertDefined(analyticId);
      return fetchSessionEventTypes(analyticId);
    },
    enabled: !!analyticId,
  });

  // Records a generic analytics event onto the current analytics session.
  const handleTrackEvent = useCallback(
    (event: { type: AnalyticsEventType; properties?: Record<string, unknown> }) => {
      const id = analyticId ?? doBotPayloadRef.current.sender;
      if (!id) return;
      trackEventMutate({ analyticId: id, eventType: event.type, properties: event.properties });
    },
    [analyticId, trackEventMutate]
  );

  // Persist the user's satisfaction survey answer (rating first, then optionally
  // feedback) onto the analytics session. analyticId is stored as the doBot sender.
  const handleSubmitSurvey = useCallback(
    async (surveyAnswer: SurveyAnswer) => {
      const analyticId = doBotPayloadRef.current.sender;
      if (!analyticId) return;
      try {
        await submitSurveyMutateAsync({ analyticId, surveyAnswer });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to submit survey answer", error);
      }
    },
    [submitSurveyMutateAsync]
  );

  // Sends the user on to the standard "bad feedback" node after a bad rating in the
  // satisfaction survey, when the survey asks for it (DAS-2868). The node's response is
  // displayed like any other bot message, with no user bubble: the user typed nothing.
  const handleBadRating = useCallback(async () => {
    if (!doBotUrlRef.current) return;
    doBotPayloadRef.current.message = BAD_FEEDBACK_COMMAND;
    doBotPayloadRef.current.metadata = {
      ...doBotPayloadRef.current.metadata,
      form: undefined,
      isStreamingEnabled: !isStreamingDisabled,
      context: {},
    };
    await askDoBot();
  }, [askDoBot, isStreamingDisabled]);

  const handleAuthTicket = useCallback(
    (ticket: string) => {
      storeTicket(dochatConfigId, ticket);
      setAuthTicket(ticket);
    },
    [dochatConfigId]
  );

  const conversationStartParamsInput = {
    brandFromQParams: brandFromQueryParams || null,
    callingURL: callingURL || null,
    chatbotStaticCtxMapKey: chatbotStaticCtxMapKey || null,
    digitalDynamicCtx: digitalDynamicCtx || null, // we send it server side to create a pincode and store it in contrack
    id: dochatConfigId,
    pinCode: pinCode || null, // we send it server side to retrieve the context associated with the pincode
    positionId: positionId || null,
    sessionId: sessionId || null,
    theming: theming || null,
    userLang: wishedLocale.current || null,
  };
  // Every fetch creates an analytics row: the query is keyed by conversation, so a new
  // conversation (on mount and after every reset) fetches once, and nothing else refetches it.
  const { isError: hasChatbotParamsError } = useQuery({
    queryKey: [
      "conversationStartParams",
      conversationStartParamsInput,
      conversationCount,
      authTicket,
    ],
    queryFn: () => fetchDochatConfig(conversationStartParamsInput, authTicket),
    staleTime: Infinity,
    // A page mounted again starts a new conversation instead of reusing the cached one.
    cacheTime: 0,
    onSuccess: ({ config, auth, session }) => {
      // The conversation starts only when the answer carries a session; otherwise the chat shows
      // its login screen, in its theme.
      if (!session) {
        // A ticket the server no longer accepts is of no use at the next start. Kept in memory,
        // so that the login screen still tells why.
        if (auth.status === "forbidden" || auth.reason === "ticket_expired") {
          forgetStoredTicket(dochatConfigId);
        }
        setAuthGate({ config, auth });
        return;
      }
      setAuthGate(null);
      const {
        analyticId,
        chatbotParams,
        company_cuid,
        computedAvailableLocales,
        computedBrand,
        computedLanguage,
        computedStartMessage,
        computedStaticData,
        createdPinCode,
        entities,
        intent,
        mergedCtxData,
        orchestrator_id,
        outOfCredits,
        rasaUrl: doBotUrl,
        maxUserMessageLength,
        redactPrivateInfos,
        satisfactionForm,
      } = session;
      setIsOutOfCredits(outOfCredits);
      if (outOfCredits) setMessages((prev) => [...prev, getOutOfCreditsErrorMessage(tCommon)]);
      setMergedCtxData(mergedCtxData);
      setIntent(intent);
      setEntities(entities ?? null);
      setAnalyticId(analyticId);
      doBotPayloadRef.current.message = computedStartMessage;
      doBotPayloadRef.current.sender = analyticId;
      doBotPayloadRef.current.redactPrivateInfos = redactPrivateInfos;
      doBotPayloadRef.current.metadata = {
        ...doBotPayloadRef.current.metadata,
        brand: computedBrand,
        clickedSolData: "",
        company_cuid,
        orchestrator_id,
        testing: isTestingMode,
        ...(simDatetime && { simDatetime }),
        context: {
          pinCode: pinCode || createdPinCode || null,
          static_data: computedStaticData,
        },
      };
      doBotPayloadRef.current.dochatConfigId = dochatConfigId;
      doBotUrlRef.current = doBotUrl;
      // This orchestrator is internationalized
      const availableLocales = computedAvailableLocales || chatbotParams.availableLocales;
      if (availableLocales) {
        chatbotParams.availableLocales = availableLocales;
        if (computedLanguage) {
          setChatLocale(computedLanguage.split("_")[0] as DoChatLocale);
        }
        localeSwitcher({
          availableLocales: availableLocales,
          defaultModelLanguage: chatbotParams.defaultModelLanguage,
          doBotPayloadRef,
          doBotUrlRef,
          requestedLocale: computedLanguage,
          setCurrentLocale,
        });
      }
      setChatbotParams(chatbotParams);
      setSatisfactionForm(satisfactionForm ?? null);
      setMaxUserMessageLength(maxUserMessageLength);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error("Failed to retrieve the chatbot parameters", error);
    },
  });

  // Display lang in HTML's root
  useEffect(() => {
    if (!chatbotParams) return;
    const html = document.querySelector<HTMLElement>("html");
    if (html) {
      const lang = wishedLocale.current || currentLocale; // wished locale is prioritized
      html.lang = String(lang?.split("_")[0] ?? "fr");
    }
  }, [chatbotParams, currentLocale, wishedLocale]);

  useEffect(() => {
    if (chatbotParams && !isOutOfCredits && doBotUrlRef.current) {
      listenForIncomingMessages();
      void askDoBot();
    }
  }, [chatbotParams, isOutOfCredits, doBotUrlRef, askDoBot, listenForIncomingMessages]);

  // Reports a solution click with a dedicated `/sol_clicked` event, so click analytics
  // no longer depend on the legacy `/trigger_rating_feedback` survey trigger (which the
  // new front-end satisfaction survey does not send). Sent as a side request: its
  // response is never displayed, and a failure must not break the conversation.
  const reportSolutionClick = useCallback(async (button: ChatFastAction) => {
    if (!doBotUrlRef.current) return;

    try {
      await sendQuestionToDoBot({
        ...doBotPayloadRef.current,
        message: buildSolutionClickCommand(button),
        metadata: {
          // The chatbot builds the event from the command payload alone, and rejects the
          // whole request when `clickedSolData` is neither "" nor a fully valid object.
          ...doBotPayloadRef.current.metadata,
          clickedSolData: "",
          // A submitted form is reported too: the form goes with its `/workflow` only.
          form: undefined,
          isStreamingEnabled: false,
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("Could not report the solution click", error);
    }
  }, []);

  const buildUserMessage = useCallback((text: string): ChatMessage => {
    messagesLengthRef.current += 1;
    return {
      blocks: [
        {
          type: ChatCustomBlockTypes.text,
          text,
          id: `message-${messagesLengthRef.current}-block-0`,
        },
      ],
      user: true,
      id: `message-${messagesLengthRef.current}`,
    };
  }, []);

  const handleSubmit = useCallback(
    async (userMessage: string | ChatFastAction) => {
      doBotPayloadRef.current.metadata = buildSubmitMetadata(
        doBotPayloadRef.current.metadata,
        userMessage,
        { isStreamingEnabled: !isStreamingDisabled }
      );
      let shouldAskDoBot = true;
      if (typeof userMessage === "string") {
        doBotPayloadRef.current.message = userMessage;
      } else {
        if (userMessage.payload) {
          doBotPayloadRef.current.message = userMessage.payload;
        } else if (satisfactionForm) {
          // Front-end satisfaction survey handles rating; skip legacy bot trigger.
          shouldAskDoBot = false;
        } else {
          doBotPayloadRef.current.message = "/trigger_rating_feedback";
        }

        const solutionClick = getSolutionClickToReport(userMessage);
        if (solutionClick) {
          void reportSolutionClick(solutionClick);
        }
      }

      const messageText =
        typeof userMessage === "string"
          ? userMessage
          : userMessage.tel
          ? `📞 ${userMessage.tel}.`
          : userMessage.title;

      if (isOnlyLastMessageDisplayed && shouldAskDoBot) {
        // onlyLastMessage: the previous bot response is replaced by the user's own
        // message, which stays visible (next to the loader) until the bot replies
        // and takes over the display in its turn.
        // Keep the replaced bot response in the history stack so the "back"
        // button can restore it.
        const previousMessages = currentMessagesRef.current;
        if (previousMessages.length > 0 && previousMessages.some((message) => !message.user)) {
          onlyLastMessageHistoryRef.current = [
            ...onlyLastMessageHistoryRef.current,
            previousMessages,
          ];
          setOnlyLastMessageHistoryLength(onlyLastMessageHistoryRef.current.length);
        }
        setMessages(messageText ? [buildUserMessage(messageText)] : []);
      } else if (messageText) {
        setMessages((previousMessages) => [...previousMessages, buildUserMessage(messageText)]);
      }

      if (shouldAskDoBot) {
        await askDoBot();
      }
      // The click is sent once: the side requests (`/changeLocale`, `/sol_clicked`) and the
      // greeting of a restarted conversation reuse this metadata, and would apply the button's
      // context actions again.
      doBotPayloadRef.current.metadata = {
        ...doBotPayloadRef.current.metadata,
        buttonCuid: undefined,
      };
    },
    [
      askDoBot,
      buildUserMessage,
      isOnlyLastMessageDisplayed,
      isStreamingDisabled,
      reportSolutionClick,
      satisfactionForm,
    ]
  );

  // Restores the previously displayed bot response in `onlyLastMessage` mode.
  // The back button is only shown once a response has fully arrived (idle
  // state), so there is no in-flight response to abort here.
  const handleOnlyLastMessageBack = useCallback(() => {
    const history = onlyLastMessageHistoryRef.current;
    if (history.length === 0) return;

    const previousMessages = history[history.length - 1];
    onlyLastMessageHistoryRef.current = history.slice(0, -1);
    setOnlyLastMessageHistoryLength(onlyLastMessageHistoryRef.current.length);

    setMessages(previousMessages);
  }, []);

  const notifyLocaleChange = useCallback(async (selectedLocale: Locale) => {
    if (!doBotUrlRef.current) return;

    try {
      await sendQuestionToDoBot({
        ...doBotPayloadRef.current,
        message: "/changeLocale",
        metadata: {
          ...doBotPayloadRef.current.metadata,
          clickedSolData: "",
          isStreamingEnabled: false,
          language: selectedLocale,
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("Could not notify locale change", error);
    }
  }, []);

  const handleChangeLocale = useCallback(
    async (selectedLocale: Locale) => {
      if (chatbotParams?.availableLocales && doBotUrlRef.current) {
        wishedLocale.current = selectedLocale;
        const nextLanguage = selectedLocale?.slice(0, 2);
        setChatLocale(nextLanguage as DoChatLocale);
        await notifyLocaleChange(selectedLocale);
        if (isTestingMode) {
          window.parent.postMessage({ action: DoChatAction.resetUsedSources }, "*");
        }
        resetState();
      }
    },
    [chatbotParams?.availableLocales, setChatLocale, isTestingMode, notifyLocaleChange, resetState]
  );

  const handleFastAction: ChatProps["onFastAction"] = useCallback(
    (resp) => {
      // When fsmDef, there's no action sent to do-bot
      if (resp.fsmDef) {
        setMessages((messages: ChatMessage[]) => {
          const lastMessage = messages.at(-1) as ChatMessage;
          const lastBlocks = lastMessage?.blocks || [];
          return [
            ...messages.slice(0, -1),
            {
              ...lastMessage,
              blocks: [
                ...lastBlocks,
                {
                  type: ChatCustomBlockTypes.steps,
                  fsmDef: resp.fsmDef,
                  title: "",
                },
              ],
            },
          ] as ChatMessage[];
        });
        return;
        // For other types, we handle it specifically and always send the action to do-bot
      } else if (resp.code) {
        const script = document.createElement("script");
        const scriptContent = document.createTextNode(resp.code);
        script.appendChild(scriptContent);
        document.body.appendChild(script);
      } else if (resp.tel) {
        if (isMobile) {
          window.open(resp.tel.match(/^tel/) ? resp.tel : `tel:${resp.tel}`);
        } else {
          void handleSubmit(resp);
          window.open(resp.tel.match(/^tel/) ? resp.tel : `tel:${resp.tel}`);
        }
      } else if (resp.url) {
        window.open(appendHttpsSchemeIfNecessary(rewrite(resp.url)));
      }
      if (!resp.tel || (resp.tel && isMobile)) {
        void handleSubmit(resp);
      }
    },
    [handleSubmit, isMobile, rewrite]
  );

  const handleHandoverRequest = useCallback(() => {
    void handleSubmit(
      `/get_response{"button_response_id":"${chatbotParams?.handover?.handoverStartCuid}"}`
    );
  }, [chatbotParams?.handover?.handoverStartCuid, handleSubmit]);

  const handleEditBotResponse: NonNullable<ChatProps["onEditBotResponse"]> = useCallback(
    (messageId: string, openDialog = false) => {
      return isTestingMode && isEditingMode
        ? window.parent.postMessage(
            { action: DoChatAction.editBotResponse, messageId, openDialog },
            "*"
          )
        : null;
    },
    [isTestingMode, isEditingMode]
  );

  const handleRefreshConversation = useCallback(() => {
    if (isTestingMode) window.parent.postMessage({ action: DoChatAction.resetUsedSources }, "*");
    resetState();
  }, [isTestingMode, resetState]);

  const title = useMemo(
    () =>
      currentLocale &&
      chatbotParams?.availableLocales &&
      chatbotParams.availableLocales[currentLocale]?.title
        ? chatbotParams.availableLocales[currentLocale].title
        : chatbotParams?.title || "do-chat",
    [currentLocale, chatbotParams]
  );

  const subtitle = useMemo(
    () =>
      currentLocale &&
      chatbotParams?.availableLocales &&
      chatbotParams.availableLocales[currentLocale]?.subTitle
        ? chatbotParams.availableLocales[currentLocale].subTitle
        : chatbotParams?.subTitle || "A DialOnce conversational agent",
    [currentLocale, chatbotParams]
  );

  // The square symbol suits an icon; the full logo only stands in when there is no symbol.
  const iconUrl = chatbotParams?.logoDataUri || chatbotParams?.fullLogoDataUri;

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta property="og:title" content={title} />
        <meta property="og:description" content={subtitle} />
        {iconUrl && <link rel="icon" href={iconUrl} />}
        {iconUrl && <meta property="og:image" content={iconUrl} />}
      </Head>
      <div className="h-full">
        {authGate ? (
          <LoginScreen
            auth={authGate.auth}
            cdnUrl={CDN_URL}
            config={authGate.config}
            dochatConfigId={dochatConfigId}
            forceDialOnceLogin={router.query["dialonce-login"] !== undefined}
            locale={wishedLocale.current || chatLocale}
            onTicket={handleAuthTicket}
          />
        ) : hasChatbotParamsError ? (
          <div className="flex min-h-screen items-center justify-center p-4">
            <p className="text-center">{tCommon("genericError")}</p>
          </div>
        ) : !chatbotParams ? (
          <LoadingScreen />
        ) : (
          <NextIntlClientProvider locale={chatLocale} messages={currentMessages}>
            <Chat
              cdnUrl={CDN_URL}
              chatbotParams={chatbotParams}
              currentLocale={currentLocale}
              disabled={disabled || isOutOfCredits}
              inputRef={chatInputRef}
              isClosable={isClosable}
              isFullSize={theming === "pci"}
              isOutOfCredits={isOutOfCredits}
              isTestingMode={isTestingMode}
              isEditingMode={isEditingMode}
              lastMessageId={lastMessageIdRef.current}
              lastUserMessageId={lastUserMessageId}
              lastUserMessageRef={lastUserMessageRef}
              mergedCtxData={mergedCtxData}
              messages={messages}
              setMessages={setMessages}
              noHeader={hasNoHeader}
              onChangeLocale={handleChangeLocale}
              onEditBotResponse={handleEditBotResponse}
              onFastAction={handleFastAction}
              onHandoverRequest={handleHandoverRequest}
              onRefreshConversation={handleRefreshConversation}
              onSubmit={handleSubmit}
              onBackToPreviousStep={
                isOnlyLastMessageDisplayed ? handleOnlyLastMessageBack : undefined
              }
              hasPreviousStep={
                isOnlyLastMessageDisplayed ? onlyLastMessageHistoryLength > 0 : undefined
              }
              backStopMessageId={liveChatEndMessageId}
              readonlyModeMessage={isOutOfCredits ? tCommon("disabledChatbotError") : undefined}
              isMobile={isMobile}
              isLoading={isLoading}
              maxUserMessageLength={maxUserMessageLength}
              isLiveChatActive={isLiveChatActiveRef.current ?? undefined}
              liveChatEndedAt={liveChatEndedAt}
              rewriteUrl={rewrite}
              satisfactionForm={satisfactionForm}
              onSubmitSurvey={handleSubmitSurvey}
              onBadRating={handleBadRating}
              onTrackEvent={handleTrackEvent}
              recordedEventTypes={recordedEventTypes}
            />
          </NextIntlClientProvider>
        )}
      </div>
    </>
  );
};

// Server-rendered on every request, so that `router.query` is filled on the first render.
export const getServerSideProps: GetServerSideProps = async () => ({ props: {} });

export default DochatConfigPage;
